/**
 * SafetyDrillsTab Component
 * Displays drill requirements and compliance status with 2/3 + 1/3 layout
 */

import { devLog } from '../../../../utils/devLog';
import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  PlusCircle,
  ClipboardList,
  Download,
  Search,
  SlidersHorizontal
} from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import Dropdown from '../../../common/Dropdown';
import DrillsSidebar from './DrillsSidebar';
import DrillsSummaryCard from './DrillsSummaryCard';
import DrillCardEnhanced from './DrillCardEnhanced';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import { calculateDrillStatus, formatDaysUntilDue, calculateDrillStats } from './utils/drillsHelpers';

const SafetyDrillsTab = ({
  drills,
  vesselRequirements,
  drillList,  // FT-112 REWRITE: Direct from useDrillsConfig (mandatory + company drills)
  onRecordDrill,
  onViewDrill,
  onHistory,
  onSettings,
  normalizeDrillRequirement,
  onExport,  // FT-387: wire export button to DrillsModule handleExportReport
}) => {
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false); // FT-16: collapse filters on mobile

  // FT-112 REWRITE: Use drillList directly from useDrillsConfig
  // This is the SINGLE SOURCE OF TRUTH - combines WBC3 mandatory + company drills
  // React handles reactivity automatically via useStorageState
  const drillItems = useMemo(() => {
    // Primary source: drillList from useDrillsConfig
    if (drillList && drillList.length > 0) {
      devLog('[SafetyDrillsTab] FT-112 REWRITE: Using drillList with', drillList.length, 'items');
      return drillList;
    }

    // Fallback to vesselRequirements for backward compatibility
    if (!vesselRequirements || vesselRequirements.length === 0) {
      devLog('[SafetyDrillsTab] No drillList or vesselRequirements');
      return [];
    }

    const normalized = vesselRequirements.map(req => {
      const norm = normalizeDrillRequirement ? normalizeDrillRequirement(req) : req;
      return {
        ...req,
        ...norm,
        drill_type: norm?.id || req.drill_type || req.type,
        drillType: norm?.id || req.drill_type || req.type,
        name: req.name || norm?.title || 'Unknown Drill',
        frequencyDays: req.frequency_days || req.frequency || 30,
        complianceLevel: req.compliance_level || (req.required ? 'Required' : 'Recommended'),
        regulatorySource: req.regulatory_source || req.citation || ''
      };
    });

    return normalized;
  }, [drillList, vesselRequirements, normalizeDrillRequirement]);
  
  // Calculate drill status for each item (from register or requirements)
  const drillStatuses = useMemo(() => {
    return drillItems.map(item => {
      const drillType = item.drillType || item.drill_type;
      const drillsOfType = drills.filter(d => d.type === drillType);
      
      // Always calculate based on actual drill history for consistency
      if (drillsOfType && drillsOfType.length > 0) {
        // We have drill history - calculate from last performed drill
        const lastDrill = drillsOfType.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        const lastDrillDate = new Date(lastDrill.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        lastDrillDate.setHours(0, 0, 0, 0);
        
        const daysSinceLastDrill = Math.floor((today - lastDrillDate) / (1000 * 60 * 60 * 24));
        const frequencyDays = item.frequencyDays || item.frequency_days || 30;
        const daysUntilDue = frequencyDays - daysSinceLastDrill;
        
        let status;
        if (daysUntilDue < 0) {
          status = 'overdue';
        } else if (daysUntilDue <= 7) {
          status = 'due_soon';
        } else {
          status = 'compliant';
        }
        
        const nextDueDate = new Date(lastDrillDate);
        nextDueDate.setDate(nextDueDate.getDate() + frequencyDays);
        
        return {
          ...item,
          status,
          daysUntilDue,
          nextDue: nextDueDate,
          lastDrillDate: lastDrill.date,
          lastDrill,
          drill_type: drillType
        };
      } else {
        // No drill history - never performed
        const frequencyDays = item.frequencyDays || item.frequency_days || 30;
        return {
          ...item,
          status: 'overdue',
          daysUntilDue: -frequencyDays, // Negative to show overdue
          nextDue: new Date(), // Due now
          lastDrillDate: null,
          lastDrill: null,
          drill_type: drillType
        };
      }
    });
  }, [drillItems, drills]);

  // Calculate overall compliance stats using shared helper
  // Industry-standard compliance = (total_required - overdue_required) / total_required × 100
  // "Due Soon" drills are NOT overdue, so they count as compliant
  // This matches MCA/ISM/BIMCO KPIs where only OVERDUE items are non-compliant
  const complianceStats = useMemo(() => {
    const stats = calculateDrillStats(drillStatuses);
    return {
      totalRequired: stats.total,
      overdue: stats.overdue,
      dueSoon: stats.dueSoon,
      compliant: stats.compliant,
      compliancePercentage: stats.compliance
    };
  }, [drillStatuses]);

  // Filter drills based on search and filter criteria
  const filteredDrills = useMemo(() => {
    return drillStatuses.filter(drill => {
      // Search filter - match name, drill type, or regulatory source
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' ||
        (drill.name || '').toLowerCase().includes(searchLower) ||
        (drill.drill_type || '').toLowerCase().includes(searchLower) ||
        (drill.drillType || '').toLowerCase().includes(searchLower) ||
        (drill.regulatorySource || '').toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = statusFilter === 'all' || drill.status === statusFilter;

      // Compliance filter
      const isRequired = drill.complianceLevel === 'Required' || drill.compliance_level === 'Required' || drill.required === true;
      const matchesCompliance = complianceFilter === 'all' ||
        (complianceFilter === 'required' && isRequired) ||
        (complianceFilter === 'recommended' && !isRequired);

      return matchesSearch && matchesStatus && matchesCompliance;
    });
  }, [drillStatuses, searchTerm, statusFilter, complianceFilter]);

  // Separate filtered drills into required and recommended
  const requiredDrills = filteredDrills.filter(d => d.complianceLevel === 'Required' || d.compliance_level === 'Required' || d.required === true);
  const recommendedDrills = filteredDrills.filter(d => d.complianceLevel === 'Recommended' || d.compliance_level === 'Recommended' || d.required === false);

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || complianceFilter !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setComplianceFilter('all');
  };

  // Get status styling
  const getStatusStyling = (status) => {
    switch (status) {
      case 'overdue':
        return {
          bgColor: 'bg-red-50',
          textColor: 'text-red-800',
          borderColor: 'border-red-200',
          icon: AlertTriangle,
          iconColor: 'text-red-600'
        };
      case 'due_soon':
        return {
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200',
          icon: Clock,
          iconColor: 'text-yellow-600'
        };
      case 'compliant':
        return {
          bgColor: 'bg-green-50',
          textColor: 'text-green-800',
          borderColor: 'border-green-200',
          icon: CheckCircle,
          iconColor: 'text-green-600'
        };
      default:
        return {
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
          icon: Clock,
          iconColor: 'text-gray-600'
        };
    }
  };

  const renderDrillCard = (drill) => {
    return (
      <DrillCardEnhanced
        key={drill.drill_type}
        drill={drill}
        onRecordDrill={onRecordDrill}
        onViewDrill={onViewDrill}
        onViewHistory={(drill) => {
          // TODO: Implement drill history view
          devLog('View history for', drill.drill_type);
        }}
      />
    );
  };

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // (Drills Overview: [+ Record] [History] [Export]). Export is a
  // placeholder no-op — matches the pre-FT-358 TODO in the old bar JSX.
  useSetBottomBarActions([
    {
      key: 'drills-record',
      icon: PlusCircle,
      label: 'Record',
      primary: true,
      onClick: () => onRecordDrill && onRecordDrill(),
    },
    {
      key: 'drills-history',
      icon: Clock,
      label: 'History',
      onClick: () => onHistory && onHistory(),
    },
    {
      key: 'drills-export',
      icon: Download,
      label: 'Export',
      onClick: () => onExport && onExport(),  // FT-387: wire to DrillsModule handleExportReport
    },
  ]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Content - 2/3 width */}
      <div className="lg:col-span-2 pb-20 lg:pb-0">
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          {/* Header with INTEGRATED search - Matches Maintenance pattern */}
          <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
            <div className="flex items-center justify-between gap-4 sm:gap-6">
              {/* Title - Responsive */}
              <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
                Safety Drills
              </h3>

              {/* Search Input - hidden on mobile, shown on desktop */}
              <div className="relative flex-1 max-w-2xl hidden sm:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search drills..."
                  aria-label="Search drills by type or status"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                />
              </div>

              {/* Mobile: Filter toggle button */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="sm:hidden flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md text-sm font-medium transition-colors"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filter
                {(statusFilter !== 'all' || complianceFilter !== 'all' || searchTerm) && (
                  <span className="h-2 w-2 bg-cyan-400 rounded-full" />
                )}
              </button>
            </div>
          </div>

          {/* Content Section */}
          <div className="px-4 sm:px-6 py-3 sm:py-5">
            <div className="space-y-3">
              {/* Mobile: Collapsible filter panel (search + dropdowns) */}
              {showMobileFilters && (
                <div className="sm:hidden space-y-3 pb-2">
                  {/* Mobile search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search drills..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-white text-slate-800 text-sm border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  {/* Mobile dropdowns */}
                  <div className="flex gap-3">
                    <Dropdown
                      value={statusFilter}
                      onChange={setStatusFilter}
                      width="flex-1"
                      options={[
                        { value: 'all', label: 'All Status' },
                        { value: 'overdue', label: 'Overdue' },
                        { value: 'due_soon', label: 'Due Soon' },
                        { value: 'compliant', label: 'Compliant' },
                      ]}
                    />
                    <Dropdown
                      value={complianceFilter}
                      onChange={setComplianceFilter}
                      width="flex-1"
                      options={[
                        { value: 'all', label: 'All Types' },
                        { value: 'required', label: 'Required' },
                        { value: 'recommended', label: 'Recommended' },
                      ]}
                    />
                  </div>
                </div>
              )}

              {/* Desktop: Always-visible filter dropdowns */}
              <div className="hidden sm:flex gap-3 mb-6">
                <Dropdown
                  value={statusFilter}
                  onChange={setStatusFilter}
                  width="flex-none w-36"
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'overdue', label: 'Overdue' },
                    { value: 'due_soon', label: 'Due Soon' },
                    { value: 'compliant', label: 'Compliant' },
                  ]}
                />
                <Dropdown
                  value={complianceFilter}
                  onChange={setComplianceFilter}
                  width="flex-none w-36"
                  options={[
                    { value: 'all', label: 'All Types' },
                    { value: 'required', label: 'Required' },
                    { value: 'recommended', label: 'Recommended' },
                  ]}
                />
              </div>
            </div>

            {/* Required Drills Section */}
            {/* FT-169: Removed "Required Drills" header - inaccurate when custom drills added */}
            <div className="mb-6">
              {requiredDrills.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    {hasActiveFilters
                      ? 'No required drills match your filters'
                      : 'No drill requirements configured for this vessel'
                    }
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="mt-2 text-sm text-cyan-600 hover:text-cyan-500"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {requiredDrills.map(renderDrillCard)}
                </div>
              )}
            </div>

            {/* Recommended Drills Section */}
            {(recommendedDrills.length > 0 || (hasActiveFilters && complianceFilter !== 'required')) && (
              <div className="mt-6">
                {/* Sub-section header */}
                <div className="bg-slate-700 px-5 py-3 mb-4 rounded-lg">
                  <h3 className="text-base font-semibold text-white">Recommended Drills</h3>
                  <p className="mt-1 text-sm text-slate-300">
                    Additional drills based on vessel operations and best practices
                  </p>
                </div>

                {recommendedDrills.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      {hasActiveFilters
                        ? 'No recommended drills match your filters'
                        : 'No recommended drills configured'
                      }
                    </p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="mt-2 text-sm text-cyan-600 hover:text-cyan-500"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recommendedDrills.map(renderDrillCard)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar - 1/3 width - Hidden on mobile */}
      <div className="hidden lg:block">
        <DrillsSidebar
          complianceStats={complianceStats}
          onRecordDrill={() => onRecordDrill()}
          onHistory={onHistory}
          onSettings={onSettings}
          drillStatuses={drillStatuses}
        />
      </div>

      {/* Mobile Summary Box - ONLY visible on mobile (<lg) */}
      <div className="lg:hidden mb-20 px-4">
        <DrillsSummaryCard complianceStats={complianceStats} />
      </div>

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}
    </div>
  );
};

export default SafetyDrillsTab;